import { Column } from 'react-data-grid';
import { ArtifactInfo } from '../types/artifacts-type';
import ArtifactCicdCell from '../components/Table/ArtifactCicdCell';
import ArtifactCodeCell from '../components/Table/ArtifactCodeCell';
import { Theme, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getArtifactIconType } from '../utils/artifacts-utils';
import ArtifactRegistryCell from '../components/Table/ArtifactRegistryCell';
import DateFormat from '../../elements/Date/DateFormat';
import ArtifactCloudCell from '../components/Table/ArtifactCloudCell';
import SeverityHeatMap from '../../elements/SeverityHeatMap/SeverityHeatMap';
import IssueBadge from '../../common/components/IssueBadge';
import ApplicationCell from '../../elements/TableCells/ApplicationCell';
import { RepoTypes } from 'common-types';
import MultiLineEllipsis from '../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { useRef } from 'react';

export const useArtifactsTable = () => {
  const { classes } = useStyles();

  const columnsRef = useRef<Column<ArtifactInfo>[]>([
    {
      key: 'issuesBreakdown',
      name: 'Issues',
      width: 90,
      formatter: ({ row }) => (
        <IssueBadge
          severitiesAlerts={row.issuesBySeverity}
          fontSize='0.68rem'
          top={2}
          tooltipPlacement='top'
        />
      ),
    },
    {
      key: 'name',
      name: 'Artifact',
      // width: 300,
      formatter: ({ row }) => {
        const ArtifactTypeIcon = getArtifactIconType(row.artifactInfo.type);
        return (
          row.artifactInfo && (
            <Tooltip
              arrow
              placement='top'
              title={row.artifactInfo.hash}
              className={classes.artifactName}>
              <div>
                <div className={classes.iconContainer}>
                  {ArtifactTypeIcon && (
                    <ArtifactTypeIcon className={classes.artifactIcon} />
                  )}
                </div>
                <MultiLineEllipsis lines={2} variant='body2'>
                  {row.artifactInfo.name}
                </MultiLineEllipsis>
              </div>
            </Tooltip>
          )
        );
      },
    },

    {
      key: 'version',
      name: 'Version/Tag',
      formatter: ({ row }) => (
        <Tooltip title={row.artifactInfo.version} arrow placement='top'>
          <Typography
            className={classes.overflow}
            variant='body2'
            color='text.primary'>
            {row.artifactInfo.version}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: 'app',
      name: 'Application',
      width: 150,
      formatter: ({ row }) => (
        <>
          {row.appDescription && (
            <ApplicationCell
              appName={
                row.appDescription.appName ? row.appDescription.appName : 'N/A'
              }
              appType={row.appDescription.appType as RepoTypes}
            />
          )}
        </>
      ),
    },

    {
      key: 'businessPriority',
      name: `Business Priority`,
      cellClass: classes.textCenter,
      width: 100,
      formatter: ({ row }) => (
        <>
          {row.appDescription && (
            <Typography
              variant='body2'
              color='text.primary'
              overflow='hidden'
              textAlign='center'>
              {Math.round(row.appDescription.businessPriority)}
            </Typography>
          )}
        </>
      ),
    },
    {
      key: 'code',
      name: 'Code',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      cellClass: classes.textCenter,
      formatter: ({ row }) => (
        <>
          {row.codeDescription && (
            <ArtifactCodeCell
              scm={row.codeDescription.scm}
              url={row.codeDescription.mergeLink}
            />
          )}
        </>
      ),
    },
    {
      key: 'cicd',
      name: 'CI/CD',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      cellClass: classes.textCenter,
      formatter: ({ row }) => (
        <>
          {row.cicdDescription && row.cicdDescription.pipelineExecution && (
            <ArtifactCicdCell
              cicdType={row.cicdDescription.type}
              url={row.cicdDescription?.pipelineExecution?.link}
            />
          )}
        </>
      ),
    },
    {
      key: 'registry',
      name: 'Registry',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      formatter: ({ row }) =>
        row.registryDescription && (
          <div className={classes.registryServices}>
            {row.registryDescription.slice(0, 1).map((registrry, index) => {
              return (
                registrry.type && (
                  <ArtifactRegistryCell
                    key={index}
                    registryType={registrry.type}
                    url={registrry.link}
                  />
                )
              );
            })}
            {row.registryDescription.length > 1 && (
              <Typography fontSize='0.68rem' color='text.primary'>
                {`+ ${row.registryDescription.length - 1}`}
              </Typography>
            )}
          </div>
        ),
    },
    {
      key: 'cloud',
      name: 'Cloud',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      formatter: ({ row }) =>
        row.cloudData && (
          <div className={classes.registryServices}>
            {row.cloudData.slice(0, 1).map((cloud, index) => {
              return (
                <ArtifactCloudCell
                  key={index}
                  cloudType={cloud.cloudDescription?.type}
                  cloudSubType={cloud.cloudDescription?.subType}
                  url={cloud.link}
                />
              );
            })}
            {row.cloudData.length > 1 && (
              <Typography fontSize='0.68rem' color='text.primary'>
                {`+ ${row.cloudData.length - 1}`}
              </Typography>
            )}
          </div>
        ),
    },
    {
      key: 'heatmap',
      name: 'PBOM',
      width: 190,
      formatter: ({ row }) => (
        <>
          {row.categories && <SeverityHeatMap data={row.categories} top={-1} />}
        </>
      ),
    },
    {
      key: 'createdAt',
      name: 'Created',
      width: 100,
      formatter: ({ row }) => (
        <>
          {row.cicdDescription?.pipelineExecution?.createTime && (
            <DateFormat
              date={new Date(row.cicdDescription.pipelineExecution.createTime)}
            />
          )}
        </>
      ),
    },
  ]);

  return columnsRef.current;
};

const useStyles = makeStyles()((theme: Theme) => ({
  textOverflow: {
    maxWidth: 120,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  textCenter: {
    justifyContent: 'center',
  },
  overflow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    display: '-webkit-box',
    whiteSpace: 'pre-wrap',
    lineClamp: 2,
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  registryServices: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  artifactName: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  artifactIcon: {
    width: '1rem',
    height: '1rem',
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  iconContainer: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
  },
}));
